import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Database, Plus, CheckCircle2, XCircle, AlertCircle, Copy, RefreshCw, ExternalLink, Tag, Search, X, Settings, Eye, List } from "lucide-react";
import { toast } from "sonner";
import { pipefyApi } from "@/lib/api-client";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { MultiSelectV2 } from "@/components/ui/multi-select-v2";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CreatedDatabase {
  id: string;
  name: string;
  description: string | null;
  organization: {
    id: string;
    name: string;
  } | null;
}

interface DatabaseItem {
  id: string;
  name: string;
  internal_id?: string;
  description: string | null;
  url: string;
  tags?: string[];
}

export default function CriarDatabasePipefyPage() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [databases, setDatabases] = useState<DatabaseItem[]>([]);
  const [organization, setOrganization] = useState<{ id: string; name: string } | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFieldDialogOpen, setIsFieldDialogOpen] = useState(false);
  const [isFieldsViewDialogOpen, setIsFieldsViewDialogOpen] = useState(false);
  const [createdDatabaseId, setCreatedDatabaseId] = useState<string | null>(null);
  const [selectedDatabaseForFields, setSelectedDatabaseForFields] = useState<DatabaseItem | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // Estados para criar campo
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldType, setFieldType] = useState("short_text");
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldDescription, setFieldDescription] = useState("");
  const [connectedRepoId, setConnectedRepoId] = useState(""); // ID do database ou pipeline para conexão
  const [loadingField, setLoadingField] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("ID copiado para a área de transferência!");
  };

  const fetchDatabases = async () => {
    setLoadingDatabases(true);
    try {
      const data = await pipefyApi.listDatabases({
        organizationId: organizationId.trim() || undefined,
      });

      if (data.success) {
        setDatabases(data.databases || []);
        setOrganization(data.organization || null);
      } else {
        throw new Error(data.error || "Erro ao listar databases");
      }
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao listar databases");
    } finally {
      setLoadingDatabases(false);
    }
  };

  useEffect(() => {
    fetchDatabases();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Por favor, informe o nome do database");
      return;
    }

    setLoading(true);

    try {
      const data = await pipefyApi.createDatabase({
        name: name.trim(),
        description: description.trim() || undefined,
        organizationId: organizationId.trim() || undefined,
      });

      if (data.success) {
        toast.success(data.message || "Database criado com sucesso!");
        // Salvar ID do database criado para criar campos
        if (data.table?.id) {
          setCreatedDatabaseId(data.table.id);
          setIsDialogOpen(false);
          setIsFieldDialogOpen(true);
        } else {
          // Se não tiver ID, apenas fechar e recarregar
          setName("");
          setDescription("");
          setIsDialogOpen(false);
          await fetchDatabases();
        }
      } else {
        throw new Error(data.error || "Erro ao criar database");
      }
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao criar database");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateField = async () => {
    if (!createdDatabaseId || !fieldLabel.trim()) {
      toast.error("Por favor, informe o nome do campo");
      return;
    }

    // Validar se é campo de conexão e se tem connectedRepoId
    if ((fieldType === 'connector_table' || fieldType === 'connector_pipe') && !connectedRepoId.trim()) {
      toast.error("Por favor, informe o ID do database ou pipeline para conexão");
      return;
    }

    setLoadingField(true);

    try {
      // Converter o tipo para o formato da API
      let apiFieldType = fieldType;
      if (fieldType === 'connector_table' || fieldType === 'connector_pipe') {
        apiFieldType = 'connector';
      }

      const data = await pipefyApi.createField({
        tableId: createdDatabaseId,
        type: apiFieldType,
        label: fieldLabel.trim(),
        required: fieldRequired,
        description: fieldDescription.trim() || undefined,
        connectedRepoId: (fieldType === 'connector_table' || fieldType === 'connector_pipe') 
          ? connectedRepoId.trim() 
          : undefined,
      });

      if (data.success) {
        toast.success(data.message || "Campo criado com sucesso!");
        // Limpar formulário de campo
        setFieldLabel("");
        setFieldType("short_text");
        setFieldRequired(false);
        setFieldDescription("");
        setConnectedRepoId("");
        // Não fechar o dialog para permitir criar mais campos
      } else {
        throw new Error(data.error || "Erro ao criar campo");
      }
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao criar campo");
    } finally {
      setLoadingField(false);
    }
  };

  const handleCloseFieldDialog = () => {
    setIsFieldDialogOpen(false);
    setCreatedDatabaseId(null);
    // Limpar formulário de database
    setName("");
    setDescription("");
    // Recarregar lista
    fetchDatabases();
  };

  const handleViewFields = async (database: DatabaseItem) => {
    setSelectedDatabaseForFields(database);
    setIsFieldsViewDialogOpen(true);
    setLoadingFields(true);
    setFields([]);

    try {
      const data = await pipefyApi.listFields({
        tableId: database.id,
      });

      if (data.success) {
        setFields(data.fields || []);
      } else {
        throw new Error(data.error || "Erro ao listar campos");
      }
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao listar campos");
    } finally {
      setLoadingFields(false);
    }
  };

  // Extrair todas as tags únicas dos databases
  const allTags = useMemo(() => {
    const tagsSet = new Set<string>();
    databases.forEach((db) => {
      db.tags?.forEach((tag) => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort().map((tag) => ({
      label: tag,
      value: tag,
    }));
  }, [databases]);

  // Filtrar databases por tags selecionadas
  const filteredDatabases = useMemo(() => {
    if (selectedTags.length === 0) {
      return databases;
    }
    return databases.filter((db) => {
      if (!db.tags || db.tags.length === 0) return false;
      return selectedTags.some((selectedTag) => db.tags?.includes(selectedTag));
    });
  }, [databases, selectedTags]);

  const columns: DataTableColumn<DatabaseItem>[] = [
    {
      key: "name",
      label: "Nome",
      searchable: true, // Garante que a coluna é pesquisável
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          {row.description && (
            <span className="text-xs text-muted-foreground truncate max-w-[300px]">
              {row.description}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "tags",
      label: "Tags",
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.tags && row.tags.length > 0 ? (
            row.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">Sem tags</span>
          )}
        </div>
      ),
    },
    {
      key: "id",
      label: "ID",
      render: (row) => (
        <div className="flex items-center gap-2">
          <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
            {row.id}
          </code>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => copyToClipboard(row.id)}
            title="Copiar ID"
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
    {
      key: "internal_id",
      label: "Internal ID",
      render: (row) => row.internal_id ? (
        <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
          {row.internal_id}
        </code>
      ) : "-",
    },
    {
      key: "actions",
      label: "Ações",
      className: "text-right",
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewFields(row)}
            title="Ver campos deste database"
          >
            <Eye className="h-3 w-3 mr-1" />
            Ver Campos
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setCreatedDatabaseId(row.id);
              setIsFieldDialogOpen(true);
            }}
            title="Criar campo neste database"
          >
            <Settings className="h-3 w-3 mr-1" />
            Criar Campo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(row.url, "_blank")}
            title="Abrir no Pipefy"
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Abrir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Databases Pipefy</h2>
          <p className="text-muted-foreground text-sm">
            Gerencie e visualize os databases da sua organização no Pipefy
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchDatabases}
            disabled={loadingDatabases}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingDatabases ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Novo Database
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Criar Novo Database</DialogTitle>
                <DialogDescription>
                  Preencha os dados para criar um novo database no Pipefy.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">
                    Nome do Database <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Ex: Orçamento 2025"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    placeholder="Propósito do database..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={loading}
                    rows={3}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="org-id">ID Organização (Opcional)</Label>
                  <Input
                    id="org-id"
                    placeholder="Deixe vazio para usar a padrão"
                    value={organizationId}
                    onChange={(e) => setOrganizationId(e.target.value)}
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Se vazio, usa a organização padrão configurada.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={loading}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={loading || !name.trim()}>
                  {loading && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
                  Criar Database
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Simples */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Databases</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{databases.length}</div>
            <p className="text-xs text-muted-foreground">
              {organization ? `Na organização ${organization.name}` : 'Carregando...'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Databases */}
      <Card>
        <CardHeader>
          <CardTitle>Listagem de Databases</CardTitle>
          <CardDescription>
            {selectedTags.length > 0
              ? `${filteredDatabases.length} database(s) com as tags selecionadas`
              : "Todos os databases disponíveis para consulta e integração."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable 
            columns={columns} 
            data={filteredDatabases} 
            loading={loadingDatabases}
            emptyText="Nenhum database encontrado."
            customFilters={
              allTags.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <MultiSelectV2
                    options={allTags}
                    value={selectedTags}
                    onChange={setSelectedTags}
                    placeholder="Filtrar por tags..."
                    className="min-w-[200px] w-[250px]"
                  />
                  {selectedTags.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTags([])}
                      className="h-8 px-2 text-muted-foreground hover:text-foreground"
                    >
                      Limpar filtros
                      <X className="ml-2 h-3 w-3" />
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          />
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Tags selecionadas:</span>
              {selectedTags.map((tag) => {
                const tagLabel = allTags.find((t) => t.value === tag)?.label || tag;
                return (
                  <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => {
                    setSelectedTags(selectedTags.filter((t) => t !== tag));
                  }}>
                    <Tag className="h-3 w-3 mr-1" />
                    {tagLabel}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para criar campos */}
      <Dialog open={isFieldDialogOpen} onOpenChange={setIsFieldDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Criar Campo no Database</DialogTitle>
            <DialogDescription>
              Adicione campos ao database criado. Você pode criar múltiplos campos.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="field-label">
                Nome do Campo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="field-label"
                placeholder="Ex: Descrição, Valor, Data"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                disabled={loadingField}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="field-type">Tipo do Campo</Label>
              <Select value={fieldType} onValueChange={setFieldType} disabled={loadingField}>
                <SelectTrigger id="field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short_text">Texto Curto</SelectItem>
                  <SelectItem value="long_text">Texto Longo</SelectItem>
                  <SelectItem value="number">Número</SelectItem>
                  <SelectItem value="currency">Moeda</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                  <SelectItem value="datetime">Data e Hora</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="phone">Telefone</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="dropdown">Lista Suspensa</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                  <SelectItem value="connector_table">Conexão Database</SelectItem>
                  <SelectItem value="connector_pipe">Conexão Pipeline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="field-description">Descrição (Opcional)</Label>
              <Textarea
                id="field-description"
                placeholder="Descrição do campo..."
                value={fieldDescription}
                onChange={(e) => setFieldDescription(e.target.value)}
                disabled={loadingField}
                rows={2}
              />
            </div>

            {/* Campo para ID de conexão (apenas para campos de conexão) */}
            {(fieldType === 'connector_table' || fieldType === 'connector_pipe') && (
              <div className="grid gap-2">
                <Label htmlFor="connected-repo-id">
                  ID do {fieldType === 'connector_table' ? 'Database' : 'Pipeline'} <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="connected-repo-id"
                    placeholder={`ID do ${fieldType === 'connector_table' ? 'database' : 'pipeline'} a conectar`}
                    value={connectedRepoId}
                    onChange={(e) => setConnectedRepoId(e.target.value)}
                    disabled={loadingField}
                  />
                  {fieldType === 'connector_table' && databases.length > 0 && (
                    <Select
                      value={connectedRepoId}
                      onValueChange={setConnectedRepoId}
                      disabled={loadingField}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Selecionar database" />
                      </SelectTrigger>
                      <SelectContent>
                        {databases.map((db) => (
                          <SelectItem key={db.id} value={db.id}>
                            {db.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {fieldType === 'connector_table' 
                    ? 'Informe o ID do database (table) que será conectado a este campo. Você pode selecionar da lista ou digitar o ID manualmente.'
                    : 'Informe o ID do pipeline que será conectado a este campo.'}
                </p>
                {fieldType === 'connector_table' && connectedRepoId && (
                  <div className="text-xs text-muted-foreground">
                    <strong>ID informado:</strong> <code className="bg-muted px-1 py-0.5 rounded">{connectedRepoId}</code>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="field-required"
                checked={fieldRequired}
                onChange={(e) => setFieldRequired(e.target.checked)}
                disabled={loadingField}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="field-required" className="cursor-pointer">
                Campo obrigatório
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseFieldDialog} disabled={loadingField}>
              Finalizar
            </Button>
            <Button onClick={handleCreateField} disabled={loadingField || !fieldLabel.trim()}>
              {loadingField && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />}
              Criar Campo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para visualizar campos */}
      <Dialog open={isFieldsViewDialogOpen} onOpenChange={setIsFieldsViewDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campos do Database</DialogTitle>
            <DialogDescription>
              {selectedDatabaseForFields?.name || "Carregando..."}
            </DialogDescription>
          </DialogHeader>
          
          {loadingFields ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Carregando campos...</span>
            </div>
          ) : fields.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <List className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum campo encontrado neste database.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground mb-4">
                Total: {fields.length} campo{fields.length !== 1 ? 's' : ''}
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {fields.map((field) => (
                  <Card key={field.id} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{field.label}</span>
                          {field.required && (
                            <Badge variant="destructive" className="text-xs">
                              Obrigatório
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>
                            <strong>Tipo:</strong> {field.type}
                          </span>
                          {field.internal_id && (
                            <span>
                              <strong>ID Interno:</strong> {field.internal_id}
                            </span>
                          )}
                        </div>
                        {field.description && (
                          <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                        )}
                        {field.options && field.options.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs font-medium text-muted-foreground">Opções:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {field.options.map((option: any, idx: number) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {typeof option === 'string' ? option : option.label || option.value}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(field.id)}
                        title="Copiar ID do campo"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFieldsViewDialogOpen(false)}>
              Fechar
            </Button>
            {selectedDatabaseForFields && (
              <Button
                variant="outline"
                onClick={() => {
                  setIsFieldsViewDialogOpen(false);
                  setCreatedDatabaseId(selectedDatabaseForFields.id);
                  setIsFieldDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Campo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
